import { put, all, takeLatest, select } from 'redux-saga/effects';

import types from './actionTypes';
import * as dashboardActions from './actions';
import * as monetizationItemsActions from '../../store/monetizationItems/actions';
import * as dailyFBInsightsActions from '../../store/facebookAd/dailyCampaignInsights/actions';
import * as dailyGoogleInsightsActions from '../../store/googleAd/dailyCampaignInsights/actions';
import * as smsMessagesActions from '../../store/smsMessages/actions';
import * as callsActions from '../../store/calls/actions';
import * as leadsActions from '../leads/actions';
import * as usersActions from '../users/actions';
import * as leadDailyInsightsActions from '../leads/dailyInsights/actions';
import * as peopleActions from '../people/actions';
import * as audiencesActions from '../audiences/actions';
import * as customersActions from '../customers/actions';
import * as dailyCustomerInsightsActions from '../customers/dailyInsights/actions';
import * as acquisitionOrdersActions from '../acquisitionOrders/actions';
import * as monetizationOrdersActions from '../monetizationOrders/actions';
import * as aiAgentsActions from '../aiAgents/actions';
import * as aiConversationsActions from '../aiConversations/actions';
import * as aiSimulationActions from '../aiSimulation/actions';
import * as aiConversationMonitorActions from '../aiConversationMonitor/actions';
import * as dailyMonetizationOrderInsightsActions from '../monetizationOrders/dailyInsights/actions';
import * as leadFieldsActions from '../leadFields/actions';
import * as leadFormsActions from '../leadForms/actions';
import * as campaignsActions from '../campaigns/actions';
import * as dailyCampaignInsightsActions from '../campaigns/dailyInsights/actions';
import * as surveysActions from '../surveys/actions';
import * as surveyFormsActions from '../surveyForms/actions';
import * as messageTemplatesActions from '../messageTemplates/actions';
import * as pushNotificationsActions from '../pushNotifications/actions';
import * as callsInsightsActions from '../../store/callInsights/actions';
import * as nextCallActions from '../nextCall/actions';
import * as suppliersActions from '../suppliers/actions';
import * as dailySaleInsightsActions from '../suppliers/dailySaleInsights/actions';
import * as dailyPublisherInsightsActions from '../suppliers/dailyPublisherInsights/actions';
import * as dailyCountriesInsightsActions from '../countries/dailyInsights/actions';

import { selectCountry } from '../../selectors/country';

import { isNullish } from '../../helpers/sharedFunction';

function* setFiltersSaga({ startDate, endDate, filters }) {
  try {
    yield put(
      monetizationItemsActions.fetchMonetizationItems(
        startDate,
        endDate,
        filters,
      ),
    );
    yield put(
      dailyFBInsightsActions.fetchDailyInsights(startDate, endDate, filters),
    );
    yield put(
      dailyGoogleInsightsActions.fetchDailyInsights(
        startDate,
        endDate,
        filters,
      ),
    );
    //SmsMessage hasn't sector or campaignId associations, retrieves sms data only when filters are no set
    isNullish(filters)
      ? yield put(smsMessagesActions.fetchSmsMessages(startDate, endDate))
      : yield put(smsMessagesActions.resetState());
    yield put(callsActions.fetchClosedCalls(startDate, endDate, filters));
    yield put(leadsActions.fetchLeads(startDate, endDate, filters));
    //yield put(callsActions.syncQueuedCalls());

    yield yield put(
      dashboardActions.setFiltersSuccess(startDate, endDate, filters),
    );
  } catch (error) {
    yield put(dashboardActions.setFiltersFailure(error));
  }
}

function* setCountrySaga({ countryId }) {
  try {
    const country = yield select(selectCountry(countryId));
    const exchangeQueuesModal =
      country && country.phoneExchange === 'restart' ? 'show' : 'unnecessary';
    yield put(
      dashboardActions.setCountrySuccess(countryId, exchangeQueuesModal),
    );
  } catch (error) {
    yield put(dashboardActions.setCountryFailure(error));
  }
}

export function* changeCountrySaga({ countryId }) {
  try {
    // Reset all domain store slices
    yield put(usersActions.resetState());
    yield put(dailyFBInsightsActions.resetState());
    yield put(dailyGoogleInsightsActions.resetState());
    yield put(leadDailyInsightsActions.resetState());
    yield put(leadsActions.resetState());
    yield put(peopleActions.resetState());
    yield put(audiencesActions.resetState());
    yield put(customersActions.resetState());
    yield put(dailyCustomerInsightsActions.resetState());
    yield put(acquisitionOrdersActions.resetState());
    yield put(monetizationOrdersActions.resetState());
    yield put(dailyMonetizationOrderInsightsActions.resetState());
    yield put(monetizationItemsActions.resetState());
    yield put(leadFieldsActions.resetState());
    yield put(leadFormsActions.resetState());
    yield put(aiAgentsActions.resetState());
    yield put(aiConversationsActions.resetState());
    // The live view's listeners hold the previous country's id: without this
    // they would keep its conversations on screen under the new country.
    yield put(aiConversationMonitorActions.resetState());
    // The simulation slice is tenant-scoped and persisted to IndexedDB with no
    // whitelist: without this the previous country's simulated lead,
    // transcript and target stay readable under the new one. Logout and
    // impersonation reset it too (store/auth/saga.js).
    yield put(aiSimulationActions.resetState());
    yield put(campaignsActions.resetState());
    yield put(dailyCampaignInsightsActions.resetState());
    yield put(surveysActions.resetState());
    yield put(surveyFormsActions.resetState());
    yield put(messageTemplatesActions.resetState());
    yield put(smsMessagesActions.resetState());
    yield put(pushNotificationsActions.resetState());
    yield put(callsActions.resetState());
    yield put(callsInsightsActions.resetState());
    yield put(nextCallActions.resetState());
    yield put(suppliersActions.resetState());
    yield put(dailySaleInsightsActions.resetState());
    yield put(dailyPublisherInsightsActions.resetState());
    yield put(dailyCountriesInsightsActions.resetState());

    // Update the countryId
    yield put(dashboardActions.changeCountrySuccess(countryId));

    // Re-sync data with the new countryId. Each reset above cancels the
    // listener of its slice, and every sync saga reads the countryId itself,
    // so these have to come after changeCountrySuccess. They cover what is
    // still mounted when the country changes, since a view asks for its data
    // only once, when it mounts: the first four are the list
    // startImpersonationSaga uses for the same reason, and the queued calls
    // feed the appointments box. Their line in setFiltersSaga stays
    // commented: there the listener is alive and the filters do not touch it.
    yield put(usersActions.syncUsers());
    yield put(campaignsActions.syncCampaigns());
    yield put(acquisitionOrdersActions.syncAcquisitionOrders());
    yield put(monetizationOrdersActions.syncMonetizationOrders());
    yield put(callsActions.syncQueuedCalls());
    // The AI agents page subscribes on mount to the agents and to the lead
    // fields its form offers: switching between two countries with the page
    // open would leave both lists empty until the next navigation.
    yield put(aiAgentsActions.syncAiAgents());
    yield put(leadFieldsActions.syncLeadFields());

    // Re-apply dashboard filters if set
    const state = yield select();
    if (state.Dashboard.startDate && state.Dashboard.endDate) {
      yield put(
        dashboardActions.setFilters(
          state.Dashboard.startDate,
          state.Dashboard.endDate,
          state.Dashboard.filters,
        ),
      );
    }
  } catch (error) {
    yield put(dashboardActions.changeCountryFailure(error));
  }
}

function* dashboardSaga() {
  yield all([
    takeLatest(types.SET_DASHBOARD_FILTERS.REQUEST, setFiltersSaga),
    takeLatest(types.SET_COUNTRY.REQUEST, setCountrySaga),
    takeLatest(types.CHANGE_COUNTRY.REQUEST, changeCountrySaga),
  ]);
}

export default dashboardSaga;
